import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import ClientHeader from "../components/ClientHeader";
import NavSidebar from "../components/NavSidebar";
import { usePageMeta } from "../components/DashboardShell";
import { useView } from "../contexts/ViewContext";
import ThinScroll from "../components/shared/ThinScroll";
import InfoTip from "../components/shared/InfoTip";
import { MetricCardShell, MetricSkeleton, MetricError, TONE_STYLES } from "../components/shared/MetricCardShell";
import AsOfDatePicker from "../components/AsOfDatePicker";
import { TONES, ICONS } from "../components/shared/tones";
import { getWeatherIntelligence, getWeatherDates, getWeatherDetail } from "../api";
import HeroNumber from "../components/weather-intelligence/shared/HeroNumber";
import BandBarChart from "../components/weather-intelligence/shared/BandBarChart";
import StatPair from "../components/weather-intelligence/shared/StatPair";
import GroupedBarChart from "../components/weather-intelligence/shared/GroupedBarChart";
import IndexTileStrip from "../components/weather-intelligence/IndexTileStrip";
import ActionList from "../components/weather-intelligence/ActionList";
import SparklineGrid from "../components/weather-intelligence/today-ahead/SparklineGrid";
import ForecastTable from "../components/weather-intelligence/today-ahead/ForecastTable";
import ChangeChipList from "../components/weather-intelligence/today-ahead/ChangeChipList";
import RainTimeline from "../components/weather-intelligence/today-ahead/RainTimeline";
import AqiStrip from "../components/weather-intelligence/today-ahead/AqiStrip";
import ChangeList from "../components/weather-intelligence/today-ahead/ChangeList";
import ExpectedVsActual from "../components/weather-intelligence/impact/ExpectedVsActual";
import WindowedPerformance from "../components/weather-intelligence/impact/WindowedPerformance";
import DualAxisBandChart from "../components/weather-intelligence/impact/DualAxisBandChart";
import BenchmarkBars from "../components/weather-intelligence/impact/BenchmarkBars";
import MetricTrendChart from "../components/weather-intelligence/impact/MetricTrendChart";
import OriginMap from "../components/weather-intelligence/guest-origin/OriginMap";
import ComparisonPair from "../components/weather-intelligence/forecast-truth/ComparisonPair";
import LineChartWithZero from "../components/weather-intelligence/forecast-truth/LineChartWithZero";
import LagBarChart from "../components/weather-intelligence/forecast-truth/LagBarChart";
import NormalsTable from "../components/weather-intelligence/season/NormalsTable";
import SeasonGrid from "../components/weather-intelligence/season/SeasonGrid";
import FrostDates from "../components/weather-intelligence/season/FrostDates";
import ClimateTrend from "../components/weather-intelligence/season/ClimateTrend";
import CoveragePanel from "../components/weather-intelligence/detail/CoveragePanel";
import DataTable from "../components/weather-intelligence/detail/DataTable";
import DetailRangePanel from "../components/weather-intelligence/detail/DetailRangePanel";

// ─── Tabs ───────────────────────────────────────────────────────────────────────
// Fixed tab order. Panel order within each tab follows the `rank` returned by the
// backend (spec §3). A tab with no panels in the response is hidden.
const TABS = [
  { key: "today",  label: "Today & Ahead" },
  { key: "impact", label: "Impact" },
  { key: "origin", label: "Guest Origin" },
  { key: "truth",  label: "Forecast Truth" },
  { key: "season", label: "Season" },
  { key: "detail", label: "Detail" },
];

// ─── Panel → Tab mapping ────────────────────────────────────────────────────────
// Maps every known panel key to its tab. null = pinned (always visible above tabs).
// Unknown keys fall into the first tab (spec §3: "Put unknown keys in the first tab").
const PANEL_TAB_MAP = {
  // Pinned — rendered above the tab bar
  today_conditions:            null,
  what_to_do:                  null,   // pinned area, above tabs

  // Today & Ahead
  what_changed:                "today",
  forward_exposure:            "today",
  forecast_movement:           "today",
  conditions_outlook:          "today",
  rain_timing:                 "today",
  air_quality:                 "today",

  // Impact
  weather_adjusted_performance:"impact",
  performance_windows:         "impact",
  weather_loss:                "impact",
  temperature_response:        "impact",
  rain_elasticity:             "impact",
  site_type_response:          "impact",
  stay_and_rate_tolerance:     "impact",
  booking_horizon_split:       "impact",
  metric_history:              "impact",
  network_benchmark:           "impact",

  // Guest Origin
  origin_markets:              "origin",
  escape_pressure:             "origin",
  market_tolerance:            "origin",

  // Forecast Truth
  promise_vs_delivery:         "truth",
  forecast_reliability:        "truth",
  weather_regret:              "truth",
  forecast_to_booking_lag:     "truth",

  // Season
  beyond_forecast:             "season",
  season_shape:                "season",
  frost_dates:                 "season",
  climate_trend:               "season",

  // Detail
  raw_daily:                   "detail",
  data_coverage:               "detail",
};

const DEFAULT_TAB = "today";
const DEFAULT_PANEL_TAB = "today"; // unknown keys go here

const getPanelTab = (panelKey) => PANEL_TAB_MAP[panelKey] ?? DEFAULT_PANEL_TAB;

// ─── Panel → Evidence component mapping ─────────────────────────────────────────
// Replace each null with the real evidence component as it's built.
// Unknown keys fall through to GenericCard.
const PANEL_COMPONENTS = {
  today_conditions:            IndexTileStrip,
  severe_alerts:               null,  // dead — rendered directly above tabs via SevereAlertsBanner, not through renderPanel because this can render nothing at all
  what_to_do:                  ActionList,
  what_changed:                ChangeList,
  forward_exposure:            ForecastTable,
  forecast_movement:           ChangeChipList,
  conditions_outlook:          SparklineGrid,
  rain_timing:                 RainTimeline,
  air_quality:                 AqiStrip,
  weather_adjusted_performance:ExpectedVsActual,
  performance_windows:         WindowedPerformance,
  weather_loss:                HeroNumber,
  temperature_response:        BandBarChart,
  rain_elasticity:             BandBarChart,
  site_type_response:          GroupedBarChart,
  stay_and_rate_tolerance:     DualAxisBandChart,
  booking_horizon_split:       StatPair,
  metric_history:              MetricTrendChart,
  network_benchmark:           BenchmarkBars,
  origin_markets:              OriginMap,
  escape_pressure:             HeroNumber,
  market_tolerance:            GroupedBarChart,
  promise_vs_delivery:         StatPair,
  forecast_reliability:        LineChartWithZero,
  weather_regret:              ComparisonPair,
  forecast_to_booking_lag:     LagBarChart,
  beyond_forecast:             NormalsTable,
  season_shape:                SeasonGrid,
  frost_dates:                 FrostDates,
  climate_trend:               ClimateTrend,
  raw_daily:                   DataTable,
  data_coverage:               CoveragePanel,
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

/** Capitalize first letter, lowercase rest. */
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/** Words that stay lowercase in American English title case (articles, coordinating conjunctions, short prepositions). */
const TITLE_CASE_MINOR = new Set([
  "a", "an", "the", "and", "but", "or", "for", "nor", "so", "yet",
  "at", "by", "in", "of", "on", "to", "up", "as", "from", "with",
]);

/** Convert a string to American English title case. */
const titleCase = (str) => {
  if (!str) return str;
  const words = str.split(" ");
  return words.map((word, i) => {
    const isFirst = i === 0;
    const isLast = i === words.length - 1;
    // Handle hyphenated words (e.g. "Day-by-day" → "Day-by-Day")
    if (word.includes("-")) {
      return word.split("-").map((part, j) => {
        const partFirst = isFirst && j === 0;
        const partLast = isLast && j === word.split("-").length - 1;
        if (partFirst || partLast) return cap(part);
        if (TITLE_CASE_MINOR.has(part.toLowerCase())) return part.toLowerCase();
        return cap(part);
      }).join("-");
    }
    // Strip trailing punctuation for minor-word check
    const core = word.toLowerCase().replace(/[^a-z].*$/, "");
    if (isFirst || isLast) return cap(word);
    if (TITLE_CASE_MINOR.has(core)) return word.toLowerCase();
    return cap(word);
  }).join(" ");
};

/** Format a YYYY-MM-DD string for display without timezone conversion. */
const fmtDisplayDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

/** Format a number with locale separators. */
const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Format currency with $ and commas, keeping cents. */
const fmtCurrency = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Opacity class based on confidence level. */
const confidenceOpacity = (confidence) => {
  if (confidence === "low") return "opacity-60";
  return "opacity-100";
};

// ─── Rating chip colours (spec §0d — condition ratings) ─────────────────────────
const RATING_COLORS = {
  "much better than usual for this park": "bg-emerald-100 text-emerald-700",
  "better than usual for this park":      "bg-green-100 text-green-700",
  "typical for this park":                "bg-yellow-100 text-yellow-700",
  "worse than usual for this park":       "bg-orange-100 text-orange-700",
  "much worse than usual for this park":  "bg-red-100 text-red-700",
  "not enough history yet":               "bg-gray-100 text-gray-400",
};

const getRatingColor = (rating) => RATING_COLORS[rating] ?? "bg-gray-100 text-gray-400";

// ─── Panel descriptions ───────────────────────────────────────────────────────
// Short subtext shown beneath each panel title explaining what the user is looking at.
const PANEL_DESCRIPTIONS = {
  today_conditions:            "Current temperature, rain chance, and five condition indices for this property.",
  severe_alerts:               "Active weather alerts that may impact your operations.",
  what_to_do:                  "Recommended actions to take based on today's conditions.",

  what_changed:                "How your key metrics have shifted over the past 7 days — performance, weather impact, and demand pressure.",
  forward_exposure:            "15-day forecast table with rough days highlighted and their driving reason.",
  forecast_movement:           "Day-by-day forecast changes — green for improved, amber for deteriorated.",
  conditions_outlook:          "Eight-day sparkline trends for comfort, fire, bugs, site, and stargazing indices.",
  rain_timing:                 "24-hour rain probability track per day with clock times for planning.",
  air_quality:                 "Air quality index across five EPA bands for health-sensitive guests.",

  weather_adjusted_performance:"Expected vs actual performance after removing weather effects.",
  performance_windows:         "30, 90, and 365-day performance windows to compare trends across timeframes.",
  weather_loss:                "Estimated ceiling of visits lost to weather — not recoverable revenue.",
  temperature_response:        "How bookings respond across temperature bands.",
  rain_elasticity:             "How bookings respond across rain probability bands.",
  site_type_response:          "Dry vs wet day performance by site type (RV, tent, cabin, etc.).",
  stay_and_rate_tolerance:     "How weather affects length of stay and rate tolerance.",
  booking_horizon_split:       "Booking lead time split for dry vs wet day arrivals.",
  metric_history:              "Multi-metric trend over time to spot patterns and anomalies.",
  network_benchmark:           "Your performance vs the property network median — no property named.",

  origin_markets:              "Where your guests come from, with diverging deltas showing market shifts.",
  escape_pressure:             "Net change in origin-market searches vs the 90-day baseline.",
  market_tolerance:            "How each origin market responds to wet vs dry conditions.",

  promise_vs_delivery:         "Promised weather vs what actually arrived, with the signed gap.",
  forecast_reliability:        "Forecast drift over time with a zero reference and volatility stats.",
  weather_regret:              "Booking averages on false-alarm rain days versus clear-forecast days, with the worst case.",
  forecast_to_booking_lag:     "How many days after a forecast signal bookings tend to arrive.",

  beyond_forecast:             "Typical conditions beyond the 15-day forecast, with riskiest dates flagged.",
  season_shape:                "12-month campable percentage showing your operational season.",
  frost_dates:                 "First and last frost dates — or 'rarely freezes' for mild climates.",
  climate_trend:               "Early-season vs recent conditions with a signed delta to spot shifts.",

  raw_daily:                   "Raw daily weather data table — the most granular view available.",
  data_coverage:               "Data source coverage and completeness for this property.",
};

// ─── Shared: PanelCard ──────────────────────────────────────────────────────────
// Wraps every panel: title, sentence, evidence slot, confidence caption.
// hideSentence: suppresses the sentence line (e.g. today_conditions already
// shows temp/rain as hero numbers inside IndexTileStrip).
// hideInsight: suppresses the blue "Insight:" box (panels whose evidence
// component already conveys the key takeaway visually).
const HIDE_INSIGHT_PANELS = new Set([
  "beyond_forecast",
  "data_coverage",
  "rain_timing",
  "forecast_movement",
  "air_quality",
  "weather_loss",
  "booking_horizon_split",
  "origin_markets",
  "weather_regret",
  "forecast_reliability",
  "forecast_to_booking_lag",
  "promise_vs_delivery",
  "frost_dates",
  "climate_trend",
]);

const PanelCard = ({ panel, children, hideSentence = false }) => (
  <div
    id={`panel-${panel.panel}`}
    className={`bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden transition-opacity ${confidenceOpacity(panel.confidence)}`}
  >
    <div className="p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {titleCase(panel.title)}
        </p>
        {panel.sample_size_label && (
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Based on {fmtNum(panel.sample_size)} {panel.sample_size_label}
            {panel.confidence === "low" && " — early signal"}
          </span>
        )}
      </div>
      {PANEL_DESCRIPTIONS[panel.panel] && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {PANEL_DESCRIPTIONS[panel.panel]}
        </p>
      )}
    </div>
    {children && <div className="px-5 pb-5">{children}</div>}
    {panel.sentence && !hideSentence && !HIDE_INSIGHT_PANELS.has(panel.panel) && (
      panel.sentence.toLowerCase().startsWith("not enough data") ? (
        /* "Not enough data" sentences render as a muted notice, not an insight box */
        <div className="px-5 pb-5">
          <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
            {panel.sentence}
          </p>
        </div>
      ) : (
        <div className="px-5 pb-5">
          <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-sm text-blue-800 dark:text-blue-300 leading-snug">
              <span className="font-semibold">Insight:</span> {panel.sentence}
            </p>
          </div>
        </div>
      )
    )}
  </div>
);

// ─── Shared: EmptyEvidence ──────────────────────────────────────────────────────
const EmptyEvidence = () => (
  <p className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
    Not enough data yet.
  </p>
);

// ─── Shared: GenericCard for unknown panel keys ────────────────────────────────
// Renders title + sentence only. Unknown panels must never crash the page (spec §3).
const GenericCard = ({ panel }) => <PanelCard panel={panel} />;

// ─── Skeleton ───────────────────────────────────────────────────────────────────
const PanelSkeleton = () => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
    <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
    <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
    <div className="h-24 bg-gray-100 dark:bg-gray-800 rounded" />
  </div>
);

// ─── Verdict banner ─────────────────────────────────────────────────────────────
// Mirrors other VerdictCard design (shared/tones.js `gray` tone + `compass`
// icon) so banners are consistent across features. WI's verdict is a single
// pre-written string from the backend, so we use the neutral `gray` tone
// (blue/indigo gradient) — the same "steady/insufficient data" treatment EI
// uses when there's no strong signal. No dismiss button because the verdict
// is the page hero (spec §3: "VERDICT — hero — largest type on the page").
//
// BACKEND IMPROVEMENT: The spec (§2/§2a) says the verdict should be "one
// sentence summarising the whole page" — a rich synthesis covering weather +
// impact + revenue. Currently the backend returns a thin weather-only summary.
// Once the backend returns a proper synthesis, this banner will display it
// verbatim — no frontend enrichment needed.
const VerdictBanner = ({ verdict, alerts, onDismiss }) => {
  const tone = TONES.gray;

  if (!verdict) return null;

  return (
    <div
      role="region"
      aria-label="Weather Intelligence summary"
      className={`relative ${tone.bg} border ${tone.border} rounded-xl p-4`}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss weather insight"
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="flex items-start gap-3">
        <svg
          className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {ICONS.compass}
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Weather Insight
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            <span className="text-gray-900 dark:text-gray-100">{verdict}</span>
          </p>
          {alerts && alerts.length > 0 && (
            <>
              {alerts.map((alert, i) => (
                <div key={i} className="flex items-start gap-3 mt-2">
                  <svg
                    className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {alert.headline}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Placeholder evidence for all other panels ──────────────────────────────────
// Replace each with the real component. Renders a small diagnostic box showing
// the component was mounted and received data.
const PlaceholderEvidence = ({ panel }) => (
  <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 text-xs text-gray-400 dark:text-gray-500">
    <span className="font-mono">{panel.panel}</span> — evidence component pending
  </div>
);

// ─── RPC error code → friendly message (C1) ─────────────────────────────────────
// Supabase RPCs return {code, details, hint, message}. Known codes map to copy;
// unknown codes fall back to a generic message. Raw message is shown muted.
const RPC_ERROR_MESSAGES = {
  "57014": "The request took too long. Try selecting a shorter date range.",
  "57P03": "Could not connect to the server. Please try again later.",
  "42501": "You don't have permission to access this data.",
  "42P01": "Data not found for this property.",
  "28P01": "Authentication failed. Please try again.",
  "PGRST204": "The requested data format is not available.",
};      

// ═══════════════════════════════════════════════════════════════════════════════════
// Main page component
// ═══════════════════════════════════════════════════════════════════════════════════
const WeatherIntelligencePage = () => {
  // ─── Auth / identity (same as EI) ──────────────────────────────────────────
  const username = localStorage.getItem("username") || "Admin";
  const view = useView();
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const isClient = view.isClient;
  const clientId = view.supabaseClientId;

  // ─── URL state ─────────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === "") next.delete(key);
      else next.set(key, String(value));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const TAB_KEYS = useMemo(() => TABS.map((t) => t.key), []);
  const tabParam = searchParams.get("tab");
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : DEFAULT_TAB;
  const setActiveTab = useCallback((tab) => updateParam("tab", tab), [updateParam]);
  const panelParam = searchParams.get("panel");

  // ─── As-of date state ──────────────────────────────────────────────────────
  const [asOfDate, setAsOfDate] = useState(null);
  const [isHistorical, setIsHistorical] = useState(false);

  const handleAsOfChange = useCallback((date) => {
    setAsOfDate(date);
    setIsHistorical(!!date);
  }, []);

  // "Not today" view: the owner picked a specific as-of date that isn't today.
  // Drives the what_to_do collapse — past recommendations aren't live guidance.
  // Local date (not UTC) so the boundary matches the calendar the owner picked.
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const isHistoricalView = Boolean(asOfDate) && asOfDate !== todayStr;

  // ─── Data state ────────────────────────────────────────────────────────────
  const initialState = { loading: false, error: "", data: null };
  const [intel, setIntel] = useState(initialState);
  const [dateOptions, setDateOptions] = useState(initialState);
  const [detailData, setDetailData] = useState(initialState);

  // Detail tab range
  const [detailFrom, setDetailFrom] = useState(null);
  const [detailTo, setDetailTo] = useState(null);

  // ─── Retry nonce ────────────────────────────────────────────────────────────
  // Bumped to retrigger every RPC useEffect (in their dependency arrays).
  const [retryNonce, setRetryNonce] = useState(0);

  // ─── Verdict dismiss (session-only — not persisted) ─────────────────────────
  const [verdictDismissed, setVerdictDismissed] = useState(false);

  // ─── Load snapshot date options ────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setDateOptions({ loading: true, error: "", data: null });
    getWeatherDates(clientId)
      .then((resp) => {
        if (cancelled) return;
        const datesData = resp?.data ?? null;
        setDateOptions({ loading: false, error: "", data: datesData });
        // Set default detail range once after dates load.
        // Default to last 7 days (well within 400-day cap).
        if (datesData?.detail_to) {
          const to = new Date(datesData.detail_to);
          const from = new Date(to);
          from.setDate(from.getDate() - 7);
          setDetailFrom(from.toISOString().slice(0, 10));
          setDetailTo(datesData.detail_to);
        }
      })
      .catch((e) => {
        if (!cancelled) setDateOptions({ loading: false, error: e?.message || "Failed to load dates", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, retryNonce]);

  // ─── Load main intelligence ────────────────────────────────────────────────
  // Refetches when client or asOfDate changes. No polling, no refresh button.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setIntel({ loading: true, error: "", data: null });
    getWeatherIntelligence(clientId, asOfDate)
      .then((resp) => {
        if (cancelled) return;
        // Unwrap: supabase.rpc returns the function result directly as resp.data.
        // If the driver wraps it, the shape would be resp.data[0].fn_get_weather_intelligence.
        // Test with console.log(resp.data) and adjust this line if needed.
        const payload = resp?.data ?? null;
        setIntel({ loading: false, error: "", data: payload });
      })
      .catch((e) => {
        if (!cancelled) setIntel({ loading: false, error: e?.message || "Failed to load weather intelligence", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, asOfDate, retryNonce]);

  // ─── Load detail range ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId || !detailFrom || !detailTo) return;
    let cancelled = false;
    setDetailData({ loading: true, error: "", data: null });
    getWeatherDetail(clientId, detailFrom, detailTo)
      .then((resp) => {
        if (!cancelled) setDetailData({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((e) => {
        if (!cancelled) setDetailData({ loading: false, error: e?.message || "Failed to load detail", data: null });
      });
    return () => { cancelled = true; };
  }, [clientId, detailFrom, detailTo, retryNonce]);

  // ─── Derive panels from response ──────────────────────────────────────────
  const panels = intel.data?.panels || [];
  const verdict = intel.data?.verdict || null;
  const parkName = intel.data?.park || "";
  const isStale = intel.data?.is_stale || false;
  const asOfDateFromResponse = intel.data?.as_of_date || "";

  // Pinned: today_conditions (always rank 1)
  const pinnedPanel = panels.find((p) => p.panel === "today_conditions") || null;

  // What-to-do — pinned area above tabs when active
  const whatToDoPanel = panels.find((p) => p.panel === "what_to_do") || null;

  // Group remaining panels by tab (excluding pinned panels)
  const panelsByTab = useMemo(() => {
    const grouped = {};
    TABS.forEach((t) => { grouped[t.key] = []; });
    panels.forEach((p) => {
      // Skip pinned panels — they render above the tab bar
      if (p.panel === "today_conditions") return;
      if (p.panel === "what_to_do") return;
      if (p.panel === "severe_alerts") return;

      const tab = getPanelTab(p.panel);
      if (tab && grouped[tab]) grouped[tab].push(p);
    });
    // Sort within each tab by rank
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.rank - b.rank);
    });
    return grouped;
  }, [panels]);

  // Hide tabs with zero panels
  const visibleTabs = useMemo(
    () => TABS.filter((t) => (panelsByTab[t.key] || []).length > 0),
    [panelsByTab],
  );

  // Default tab = the one containing the highest-ranked non-pinned panel
  const defaultTab = useMemo(() => {
    let best = null;
    panels.forEach((p) => {
      if (p.panel === "today_conditions" || p.panel === "severe_alerts" || p.panel === "what_to_do") return;
      if (!best || p.rank < best.rank) best = p;
    });
    return best ? getPanelTab(best.panel) : DEFAULT_TAB;
  }, [panels]);

  // Reset tab on property change
  const prevClientId = useRef(clientId);
  useEffect(() => {
    if (prevClientId.current !== clientId) {
      setActiveTab(defaultTab);
      setAsOfDate(null);
      setIsHistorical(false);
      prevClientId.current = clientId;
    }
  }, [clientId, defaultTab, setActiveTab]);

  // ─── Deep-link scroll to a panel ───────────────────────────────────────────
  useEffect(() => {
    if (!panelParam) return;
    // Wait a tick for the DOM to render the panel
    const timer = setTimeout(() => {
      const el = document.getElementById(`panel-${panelParam}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(timer);
  }, [panelParam, panels]);

  // ─── Render a single panel ─────────────────────────────────────────────────
  const renderPanel = (panel, evidenceProps = {}) => {
    // PanelCard is the safety net for every panel (spec §5/§8).
    // null/empty facts is treated the same as confidence "insufficient":
    // title + sentence only, no evidence component (avoids undefined/NaN/blank).
    const hasFacts = panel.facts && Object.keys(panel.facts).length > 0;
    if (panel.confidence === "insufficient" || !hasFacts) {
      return <PanelCard key={panel.panel} panel={panel} />;
    }
    const Evidence = PANEL_COMPONENTS[panel.panel];
    // Unknown panel key → GenericCard (title + sentence, no evidence) (spec §3/§8).
    // Handles future panels that ship without frontend support without crashing.
    if (!Evidence) {
      return <GenericCard key={panel.panel} panel={panel} />;
    }
    return (
      <PanelCard key={panel.panel} panel={panel} hideSentence={panel.panel === "today_conditions" || panel.panel === "what_to_do" || panel.panel === "raw_daily"}>
        <Evidence panel={panel} {...evidenceProps} />
      </PanelCard>
    );
  };

  // ─── State flags ───────────────────────────────────────────────────────────
  const isLoading = intel.loading;
  const isNoData = !intel.loading && (intel.data?.status === "no_data" || (intel.data && panels.length === 0));
  const detailRange = dateOptions.data || {};

  // ─── Render ────────────────────────────────────────────────────────────────
  // The As-of picker lives in the header nav bar (via rightSlot) — the global
  // date-range picker is hidden on this route (NO_GLOBAL_DATE_RANGE_ROUTES), so
  // the As-of picker takes its slot. Moved from the page body in Epic 7.
  const headerRightSlot = (
    <AsOfDatePicker
      snapshotDates={dateOptions.data?.snapshot_dates || []}
      asOfDate={asOfDate}
      onChange={handleAsOfChange}
    />
  );

  usePageMeta({ headerActive: 'Weather', sidebarActive: 'weather', rightSlot: headerRightSlot }, [headerRightSlot]);
  return (
    <main className="flex-1 min-w-0">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">

            {/* ─── Page header ──────────────────────────────────────────────── */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Weather Intelligence
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  How weather is affecting your business — and what to do about it.
                </p>
              </div>
            </div>

            {/* ─── No-data state ───────────────────────────────────────────── */}
            {isNoData && (
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-10 text-center">
                <p className="text-lg text-gray-500 dark:text-gray-400">
                  Weather intelligence is still being set up for this property.
                </p>
              </div>
            )}

            {/* ─── Loading skeletons ───────────────────────────────────────── */}
            {isLoading && (
              <div className="space-y-4">
                <PanelSkeleton />
                <PanelSkeleton />
                <PanelSkeleton />
              </div>
            )}

            {/* ─── RPC error ───────────────────────────────────────────────── */}
            {!isLoading && intel.error && (
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-red-200 dark:border-red-800 p-6">
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                  Failed to load weather intelligence
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{intel.error}</p>
              </div>
            )}

            {/* ─── Loaded content ─────────────────────────────────────────── */}
            {!isLoading && !isNoData && !intel.error && (
              <>
                {/* ─── Verdict (hero, above everything) ──────────────────── */}
                {isLoading ? (
                  <div className={`relative ${TONES.gray.bg} border ${TONES.gray.border} rounded-xl p-4 animate-pulse`}>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded bg-gray-200/50 dark:bg-gray-700/50 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="h-4 w-28 bg-gray-200/50 dark:bg-gray-700/50 rounded mb-2" />
                        <div className="h-4 w-3/4 bg-gray-200/50 dark:bg-gray-700/50 rounded" />
                      </div>
                    </div>
                  </div>
                ) : (
                  !verdictDismissed && <VerdictBanner verdict={verdict} alerts={panels.find((p) => p.panel === "severe_alerts")?.facts?.alerts} onDismiss={() => setVerdictDismissed(true)} />
                )}

                {/* ─── Pinned: today conditions ──────────────────────────── */}
                {pinnedPanel && renderPanel(pinnedPanel)}

                {/* ─── Pinned: what to do ────────────────────────────────── */}
                {whatToDoPanel && whatToDoPanel.confidence !== "insufficient" && (
                  renderPanel(whatToDoPanel, { isHistorical: isHistoricalView })
                )}

                {/* ─── Tab bar ───────────────────────────────────────────── */}
                <div className="border-b border-gray-200 dark:border-gray-800 scroll-mt-24">
                  <ThinScroll className="flex gap-1 overflow-x-auto pb-1.5 md:pb-0 -mb-px" role="tablist">
                    {visibleTabs.map((t) => {
                      const active = activeTab === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setActiveTab(t.key)}
                          className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                            active
                              ? "border-[#32B1FF] text-[#32B1FF]"
                              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </ThinScroll>
                </div>

                {/* ─── Tab content — panels in rank order ───────────────── */}
                <div className="space-y-4">
                  {(panelsByTab[activeTab] || []).map((panel) => (
                    <React.Fragment key={panel.panel}>
                      {renderPanel(panel)}
                      {/* Detail tab: inline detail range table after raw_daily */}
                      {activeTab === "detail" && panel.panel === "raw_daily" && (
                        <DetailRangePanel
                          detailData={detailData}
                          detailRange={detailRange}
                          onFromChange={setDetailFrom}
                          onToChange={setDetailTo}
                          detailFrom={detailFrom}
                          detailTo={detailTo}
                          onRetry={() => setRetryNonce((n) => n + 1)}
                        />
                      )}
                    </React.Fragment>
                  ))}
                  {(panelsByTab[activeTab] || []).length === 0 && (
                    <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
                      No panels in this category for this property.
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        </main>
  );
};

export default WeatherIntelligencePage;
